<template>
  <div class="feedback-card">
    <div class="card-actions">
      <div v-if="!isEditing" class="action-btn" @click="startEdit">
        <van-icon name="edit" size="18" color="#999" />
      </div>
      <div v-if="!isEditing" class="action-btn" @click="$emit('delete')">
        <van-icon name="delete-o" size="18" color="#999" />
      </div>
      <div v-if="isEditing" class="action-btn" @click="cancelEdit">
        <span style="font-size: 14px; color: #666;">{{ $t('cancel') }}</span>
      </div>
      <div v-if="isEditing" class="action-btn" @click="saveEdit">
        <span style="font-size: 14px; color: #aa341b; font-weight: bold;">{{ $t('btn_save') }}</span>
      </div>
    </div>

    <!-- Key Topic/Question -->
    <div v-if="item.question || item.topic_name || isEditing" class="section top-section">
      <div
        class="label topic-text"
        :class="{ 'editing': isEditing }"
        @click="isEditing ? $emit('change-topic') : null"
      >
        <template v-if="item.question || item.topic_name">
          <van-icon name="question-o" color="#333" size="16" style="margin-right: 8px" />
          <div class="topic-content-wrapper">
            <span class="topic-content">{{ item.question || item.topic_name }}</span>
            <div v-if="item.description" class="topic-desc">{{ item.description }}</div>
          </div>
        </template>
        <template v-else>
          <van-icon name="plus" color="#aa341b" size="16" style="margin-right: 8px" />
          <span class="topic-content" style="color: #aa341b; font-weight: 400;">{{ $t('add_related_question') }}</span>
        </template>

        <van-icon v-if="isEditing" name="arrow" color="#aa341b" size="14" style="margin-left: auto;" />
      </div>
    </div>

    <!-- Audio Player -->
    <div v-if="item.localId || item.feedback_voice_source" class="audio-wrapper">
      <AudioPlayer
        :local-id="item.localId"
        :src="s3AudioUrl || item.feedback_voice_source"
        :duration="item.duration || item.feedback_voice_seconds"
      />
    </div>

    <!-- Smart Summary / Content -->
    <div class="section content-row">
      <van-icon name="chat-o" class="left-icon" color="#333" size="16" />
      <div class="right-content">
        <div v-if="tags.length" class="tags">
          <span v-for="(tag, index) in tags" :key="index" class="tag">{{ tag }}</span>
        </div>

        <!-- Editable Text Area -->
        <div v-if="isEditing && canEditText" class="edit-wrapper">
          <van-field
            v-model="editContent"
            rows="2"
            autosize
            type="textarea"
            :placeholder="$t('input_feedback_placeholder')"
            :border="true"
            class="edit-field"
          />
        </div>
        <!-- Read-only Text -->
        <div v-else class="content-text summary-text">
          <div v-if="summaryContent" class="summary-block">
            <div style="white-space: pre-wrap;">{{ summaryContent }}</div>
            <div v-if="item.feedback_summary && !item.isAiLoading && !isDirectChoice" class="ai-footer">
              <van-icon name="passed" color="#aaa" size="14" style="margin-right: 4px" />
              <span>{{ $t('ai_summary_label') }}</span>
            </div>
          </div>
          <div v-if="showOriginalContent" class="original-content">
            <div class="original-toggle" @click="originalContentExpanded = !originalContentExpanded">
              <span class="original-label">{{ $t('original_feedback_label') }}</span>
              <van-icon :name="originalContentExpanded ? 'arrow-up' : 'arrow-down'" color="#999" size="14" />
            </div>
            <div v-if="originalContentExpanded" class="original-body">{{ originalContent }}</div>
          </div>
          <div v-if="item.isAiLoading" class="typing-indicator">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {wecomSDK} from '@/utils/wecom';
import { getS3FileUrl } from '@/api/kol';
import AudioPlayer from './AudioPlayer.vue';

export default {
  components: { AudioPlayer },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tags: [],
      isEditing: false,
      editContent: '',
      originalContentExpanded: false,
      s3AudioUrl: ''
    };
  },
  computed: {
    canEditText() {
      // Input text can be modified, but Voice content cannot
      // Voice content is identified by localId (client side new) or feedback_voice_seconds > 0 (server side)
      const hasVoice = this.item.localId ||
                       (this.item.feedback_voice_seconds && this.item.feedback_voice_seconds > 0) ||
                       (this.item.feedback_voice_media_id && this.item.feedback_voice_media_id.length > 0);
      return !hasVoice;
    },
    isDirectChoice() {
      return this.item.question_type === 'single' || this.item.question_type === 'multiple';
    },
    summaryContent() {
      const summary = this.item.feedback_summary;
      if (!summary) return '';

      const extractedSummary = this.extractAiSection(summary, [
        '一｜合规后的拜访反馈总结',
        '合规后的拜访反馈总结',
        'Compliant Visit Feedback Summary',
        'Compliance-adjusted Visit Feedback Summary',
        'Compliance adjusted Visit Feedback Summary',
        'Visit Feedback Summary',
        'Call Feedback Summary'
      ]);
      if (extractedSummary) return extractedSummary;

      // If no headers found (plain text or legacy), return full summary
      return summary;
    },
    originalContent() {
      return this.item.feedback_content || this.item.feedback_transcript || '';
    },
    showOriginalContent() {
      if (!this.originalContent) return false;
      if (!this.summaryContent) return true;
      return this.originalContent.trim() !== this.summaryContent.trim();
    },
    feedbackIdentity() {
      return this.item.physician_mr_call_key_feedback_id ||
             this.item.localId ||
             this.item.feedback_voice_media_id ||
             '';
    }
  },
  watch: {
    item: {
      handler(val) {
        if (val.tags && val.tags.length) {
          this.tags = val.tags;
        } else {
          this.tags = [];
        }
        this.fetchAudioUrl();
      },
      immediate: true,
      deep: true
    },
    feedbackIdentity() {
      this.originalContentExpanded = false;
    },
  },
  methods: {
    extractAiSection(text, headings = []) {
      if (!text || !headings.length) return '';

      const escapedHeadings = headings
        .filter(Boolean)
        .map(title => title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

      if (!escapedHeadings.length) return '';

      const pattern = new RegExp(`【\\s*(?:\\d+\\s*[|｜]\\s*)?(?:${escapedHeadings.join('|')})\\s*】\\s*([\\s\\S]*?)(?=\\s*【|$)`, 'i');
      const match = text.match(pattern);
      return match && match[1] ? match[1].trim() : '';
    },
    async fetchAudioUrl() {
      if (
        this.item.feedback_voice_source &&
        !this.item.localId &&
        !this.item.feedback_voice_source.startsWith('http')
      ) {
        try {
          const res = await getS3FileUrl(this.item.feedback_voice_source);
          this.s3AudioUrl = res;
        } catch (e) {
          console.error(e);
        }
      } else {
        this.s3AudioUrl = '';
      }
    },
    startEdit() {
      this.editContent = this.item.feedback_content;
      this.isEditing = true;
      this.$emit('edit-start');
    },
    cancelEdit() {
      this.isEditing = false;
      this.editContent = '';
    },
    saveEdit() {
      this.$emit('update', {
        ...this.item,
        feedback_content: this.canEditText ? this.editContent : this.item.feedback_content
      });
      this.isEditing = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.feedback-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 16px 16px 40px 16px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.card-actions {
  position: absolute;
  bottom: 8px;
  left: 26px;
  z-index: 10;
  display: flex;

  .action-btn {
    padding: 6px;
    margin-left: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
}

.top-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.audio-wrapper {
  width: calc(100% - 24px);
  margin: 10px 0;
}

.delete-btn {
  /* Replaced by card-actions */
  display: none;
}

.section {
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }

  .label {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: bold;
    color: #000;
    margin-bottom: 8px;

    &.editing {
      background: #f9f9f9;
      padding: 8px;
      border-radius: 8px;
      border: 1px dashed #aa341b;
      margin: -8px -8px 0px -8px;
    }

    .van-icon {
      margin-top: 0;
    }

    .topic-content-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;

      .topic-content {
        color: #333;
      }

      .topic-desc {
        font-size: 11px;
        color: #666;
        font-weight: normal;
        margin-top: 4px;
        line-height: 1.4;
        white-space: pre-wrap;
      }
    }
  }

  .topic-text {
    color: #666;
  }
}

.content-row {
  display: flex;
  align-items: flex-start;
  margin-top: 12px;

  .left-icon {
    margin-right: 8px;
    margin-top: 2px;
  }

  .right-content {
    flex: 1;
  }

  .content-text {
    font-size: 14px;
    color: #333;
    line-height: 1.5;
    white-space: pre-wrap;

    .original-content {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #f5f5f5;
      color: #666;
    }

    .original-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 24px;
      cursor: pointer;
      white-space: normal;
    }

    .original-label {
      font-size: 12px;
      color: #999;
    }

    .original-body {
      margin-top: 6px;
      white-space: pre-wrap;
    }
  }

  .edit-wrapper {
    width: 100%;

    .edit-field {
      padding: 4px;
      background: #f9f9f9;
      border-radius: 4px;
      border: 1px solid #eee;
    }
  }
}

.tags {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 4px;

  .tag {
    background: #fceceb; // Light red
    color: #333;
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 12px;
    margin-right: 8px;
    margin-bottom: 4px;
  }
}

.ai-footer {
  margin-top: 8px;
  display: flex;
  align-items: center;

  span {
    font-size: 12px;
    color: #aaa;
  }
}
.typing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 0;
  vertical-align: middle;
  height: 20px;
  margin-top: 5px;

  .dot {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    background-color: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}</style>
